import { useEffect, useState } from "react";
import { api, fmtDateRelative } from "../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function Tasks() {
    const [tasks, setTasks] = useState([]);
    const [deals, setDeals] = useState([]);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("open");
    const [dealFilter, setDealFilter] = useState("all");
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const d = searchParams.get("deal");
        if (d) setDealFilter(d);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = async () => {
        const [t, d] = await Promise.all([api.get("/tasks"), api.get("/deals")]);
        setTasks(t.data);
        setDeals(d.data);
    };
    useEffect(() => { refresh(); }, []);

    const toggle = async (task) => {
        await api.patch(`/tasks/${task.id}`, { completed: !task.completed });
        refresh();
    };
    const del = async (id) => {
        await api.delete(`/tasks/${id}`);
        refresh();
    };

    const filtered = tasks
        .filter((t) => filter === "all" ? true : filter === "open" ? !t.completed : t.completed)
        .filter((t) => dealFilter === "all" || t.deal_id === dealFilter);

    const dealFilterLabel = dealFilter === "all"
        ? null
        : (deals.find((d) => d.id === dealFilter)?.company_name || "Selected deal");

    return (
        <div data-testid="tasks-page">
            <PageHeader title="Tasks" subtitle={
                dealFilterLabel
                    ? `Filtered by ${dealFilterLabel} · ${filtered.length} shown`
                    : `${tasks.filter(t => !t.completed).length} open · ${tasks.filter(t => t.completed).length} completed`
            }>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-36" data-testid="tasks-filter-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={dealFilter} onValueChange={setDealFilter}>
                    <SelectTrigger className="w-56" data-testid="tasks-deal-filter-select">
                        <SelectValue placeholder="All deals" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All deals</SelectItem>
                        {deals.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.company_name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="new-task-btn">
                            <Plus className="w-4 h-4 mr-1.5" /> New Task
                        </Button>
                    </DialogTrigger>
                    <NewTaskDialog deals={deals} onCreated={() => { setOpen(false); refresh(); }} />
                </Dialog>
            </PageHeader>

            <div className="p-6 lg:p-8">
                {filtered.length === 0 ? (
                    <div className="border border-dashed border-border rounded-md p-12 text-center">
                        <p className="text-sm text-muted-foreground">No tasks in this view.</p>
                    </div>
                ) : (
                    <div className="border border-border rounded-md bg-white overflow-hidden">
                        <ul className="divide-y divide-border" data-testid="tasks-list">
                            {filtered.map((t, i) => (
                                <motion.li
                                    key={t.id}
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: Math.min(i * 0.025, 0.25), duration: 0.25, ease: "easeOut" }}
                                    whileHover={{ backgroundColor: "rgba(245,245,245,0.5)" }}
                                    className="flex items-start gap-3 px-4 py-3"
                                    data-testid={`task-row-${t.id}`}
                                >
                                    <Checkbox checked={t.completed} onCheckedChange={() => toggle(t)} className="mt-1" data-testid={`task-toggle-${t.id}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`font-medium text-sm ${t.completed ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
                                            {t.auto_generated && <Badge variant="outline" className="text-[9px] font-mono"><Zap className="w-2.5 h-2.5 mr-1" />Auto</Badge>}
                                            <Badge variant="outline" className="text-[9px] font-mono capitalize">{t.priority}</Badge>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                            {t.deal_name && (
                                                <Link to={`/deals/${t.deal_id}`} className="hover:text-foreground underline underline-offset-2" data-testid={`task-deal-link-${t.id}`}>
                                                    {t.deal_name}
                                                </Link>
                                            )}
                                            {t.due_date && <span className="font-mono">Due {new Date(t.due_date).toLocaleDateString()}</span>}
                                            <span className="font-mono">Created {fmtDateRelative(t.created_at)}</span>
                                        </div>
                                        {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                                    </div>
                                    <Button size="icon" variant="ghost" onClick={() => del(t.id)}>
                                        <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                    </Button>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

function NewTaskDialog({ deals, onCreated }) {
    const [form, setForm] = useState({ title: "", deal_id: "", due_date: "", priority: "medium", description: "" });
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                title: form.title,
                deal_id: form.deal_id || null,
                due_date: form.due_date || null,
                priority: form.priority,
                description: form.description || null,
            };
            await api.post("/tasks", payload);
            toast.success("Task created");
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DialogContent data-testid="new-task-dialog">
            <DialogHeader><DialogTitle className="font-display">Create task</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title *</Label>
                    <Input required value={form.title} onChange={setF("title")} data-testid="task-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Linked deal</Label>
                        <Select value={form.deal_id} onValueChange={setF("deal_id")}>
                            <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                            <SelectContent>
                                {deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.company_name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Priority</Label>
                        <Select value={form.priority} onValueChange={setF("priority")}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Due date</Label>
                    <Input type="date" value={form.due_date} onChange={setF("due_date")} />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                    <Textarea rows={3} value={form.description} onChange={setF("description")} />
                </div>
                <DialogFooter>
                    <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="submit-new-task-btn">
                        {loading ? "Creating…" : "Create task"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
